import { useEffect } from "react";
import {
  useConfigStore,
  useContactLensesStore,
  useStepStore,
  useStoreActions,
} from "@/store";
import { useTranslation } from "@/i18n";
import Card from "@/components/UI/Card/Card";
import Toggle from "@/components/UI/Toggle/Toggle";
import Button from "@/components/UI/Button/Button";
import styles from "./InsuranceCard.module.css";

interface InsuranceCardProps {
  className?: string;
}

export default function InsuranceCard({ className }: InsuranceCardProps) {
  const { config } = useConfigStore();
  const { t } = useTranslation();
  const { isInsuranceLoading, isInsuranceActive } = useStepStore();
  const { setIsInsuranceLoading, setIsInsuranceActive } = useStoreActions();
  const { actions } = useContactLensesStore();

  useEffect(() => {
    const insuranceModule = config?.insuranceModule;
    if (!insuranceModule) return;

    const handleInsuranceEnabled = async () => {
      setIsInsuranceActive(true);
      setIsInsuranceLoading(true);
      const insDiscount = await insuranceModule.getInsuranceDiscounts(
        config.contactLensesData,
      );
      actions.setInsuranceDiscount(insDiscount);
      setIsInsuranceLoading(false);
    };

    const handleInsuranceDisabled = () => {
      setIsInsuranceActive(false);
      actions.setInsuranceDiscount(undefined);
    };

    if (typeof insuranceModule.isEnabled === "function") {
      setIsInsuranceActive(insuranceModule.isEnabled());
      if (insuranceModule.isEnabled()) {
        handleInsuranceEnabled();
      }
    }

    const events = insuranceModule.getEvents();

    window.addEventListener(events.INSURANCE_ENABLED, handleInsuranceEnabled);
    window.addEventListener(events.INSURANCE_DISABLED, handleInsuranceDisabled);

    return () => {
      window.removeEventListener(
        events.INSURANCE_ENABLED,
        handleInsuranceEnabled,
      );
      window.removeEventListener(
        events.INSURANCE_DISABLED,
        handleInsuranceDisabled,
      );
    };
  }, [config]);

  const handleToggle = async (newChecked: boolean) => {
    const insuranceModule = config?.insuranceModule;
    if (!insuranceModule) return;

    try {
      if (newChecked) {
        insuranceModule.openInsurancePanel();
      } else {
        insuranceModule.removeInsuranceBenefits();
      }
    } catch (error) {
      console.error("Error toggling insurance:", error);
      setIsInsuranceLoading(false);
    }
  };

  const handleViewBenefits = () => {
    const insuranceModule = config?.insuranceModule;
    if (!insuranceModule) return;
    insuranceModule.openInsurancePanel();
  };

  const cardClassName = `${styles.insuranceCard} ${
    isInsuranceActive ? styles.enabled : ""
  } ${className || ""}`.trim();

  return (
    <Card className={cardClassName}>
      {!isInsuranceActive ? (
        <>
          <span className={styles.textDisabled}>
            {t("insurance.applyBenefits")}
          </span>
          <Toggle
            checked={isInsuranceActive}
            onCheckedChange={handleToggle}
            loading={isInsuranceLoading}
            aria-label={t("insurance.applyBenefits")}
          />
        </>
      ) : (
        <>
          <span className={styles.textEnabled}>
            {t("insurance.benefitsApplied")}
          </span>
          <Button
            variant="link"
            onClick={handleViewBenefits}
            className={styles.viewLink}
          >
            {t("insurance.viewBenefits")}
          </Button>
          <Toggle
            checked={isInsuranceActive}
            onCheckedChange={handleToggle}
            loading={isInsuranceLoading}
            aria-label={t("insurance.benefitsApplied")}
          />
        </>
      )}
    </Card>
  );
}
